// components/ImageGeo/ImageGeo.js

import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { MapContainer, TileLayer, ZoomControl } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import { calculateMapCenter } from "../../utils/mapUtils";
import { useMapTiles } from "../../hooks/useMapTiles";
import MapControls from "./MapControls";
import GeoMarker from "./GeoMarker";
import "./ImageGeo.css";
import L from "leaflet";
/**
 * Composant de carte géographique
 * */
const ImageGeo = ({ images, onOpenFullImage }) => {
  const [darkMode, setDarkMode] = useState(false);
  const mapRef = useRef(null);

  // Filtrage des images valides (qui ont des coordonnées)
  /*const imagesWithCoords = useMemo(
    () => images.filter((i) => i.lat && i.lon),
    [images]
  );
*/
  const imagesWithCoords = useMemo(
    () => images.filter((i) => i.hasCoords),
    [images]
  );

  // Calcul du centre de la carte
  const { center, hasValidCenter } = useMemo(
    () => calculateMapCenter(imagesWithCoords),
    [imagesWithCoords]
  );
  //console.log("center",center);
  // Gestion des tuiles selon le mode (clair/sombre)
  const { url, attribution } = useMapTiles(darkMode);

  // Gestion dynamique du resize
  const handleMapCreated = useCallback((map) => {
    mapRef.current = map;
    requestAnimationFrame(() => map.invalidateSize());

    // Masquer le logo Leaflet dans l'attribution
    if (map.attributionControl) {
      map.attributionControl.setPrefix(false);
    }
  }, []);

  // Invalidate size quand le mode ou les images changent
  /*useEffect(() => {
    const m = mapRef.current;
    if (m) m.invalidateSize();
  }, [darkMode, imagesWithCoords]);
*/
  useEffect(() => {
    const map = mapRef.current;

    // Pas de carte encore montée
    if (!map) return;

    // Pas de points
    if (!imagesWithCoords || imagesWithCoords.length === 0) {
      map.setView([46.5, -1.8], 6);
      return;
    }

    // Construire les bounds
    const bounds = L.latLngBounds(
      imagesWithCoords.map((img) => [img.coords.lat, img.coords.lng])
    );

    // Ajuster la vue
    map.fitBounds(bounds, {
      padding: [40, 40], // marge autour des points
      maxZoom: 17, // ne pas zoomer trop près
      animate: true,
    });
  }, [imagesWithCoords]);

  return (
    <div className="image-geo-container">
      <MapControls
        darkMode={darkMode}
        toggleDark={() => setDarkMode((d) => !d)}
      />

      <MapContainer
        center={hasValidCenter ? center : [46.5, -1.8]}
        zoom={hasValidCenter ? 14 : 6}
        zoomControl={false}
        attributionControl={false}
        style={{ height: "100%", width: "100%" }}
        whenCreated={handleMapCreated}
      >
        <TileLayer url={url} attribution={attribution} />
        <ZoomControl position="topright" />

        <MarkerClusterGroup chunkedLoading disableClusteringAtZoom={12}>
          {imagesWithCoords.map((img, idx) => (
            <GeoMarker
              key={`${img.id}-${idx}`}
              img={img}
              darkMode={darkMode}
              onOpenFullImage={onOpenFullImage}
            />
          ))}
        </MarkerClusterGroup>
      </MapContainer>
    </div>
  );
};

export default ImageGeo;
